import React, { ReactNode, useEffect, useState, useCallback, useRef } from 'react';
import { Send20Regular } from "@fluentui/react-icons";
import { ToolCalled } from '../../Types/Chat/ToolCalled';
import { ChatProgress } from '../../Types/Chat/ChatProgress';
import { ChatBubble } from './ChatBubble';
import { getAnimatedText } from './AnimatedText';
import { userId } from "../../App";
import { getAssistantGreetings, getChatHistoryBySession, getChatProgress, getPendingQueryId, postChatMessage } from '../../services/AssistantService';
import { v4 as uuidv4 } from 'uuid';
import { Spinner } from '@fluentui/react-components';

interface ChatMessage {
    id: string;
    role: string;
    message: string;
    dataSources?: { name: string, data: string, url: string | undefined }[];
    elements: ReactNode[];
    canBeRated: boolean;
    sessionId: number;
    queryId?: string | null;
    progress?: ChatProgress;
}

interface AssistantChatProps {
    sessionId?: number;
}

export function AssistantChat(props: AssistantChatProps) {
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingNewHistory, setIsLoadingNewHistory] = useState(false);
    const [chatHistory, setChatHistory] = useState([] as ChatMessage[]);
    const requestControllerRef = useRef<AbortController | null>(null);
    const activeRequestIdRef = useRef<string | null>(null);

    // Add scroll functionality similar to AssistantChatSide
    const scrollToBottomThrottled = useCallback(() => {
        // For AssistantChat, we can just scroll to bottom since it uses flex-col-reverse
        const container = document.querySelector('.overflow-y-auto.flex-grow.flex.flex-col-reverse');
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, []);

    useEffect(() => {
        (async () => {
            while (!userId || userId === "") {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            setIsLoadingNewHistory(true);
            var historyResponse = await getChatHistoryBySession(userId, props.sessionId ?? 0);
            const updatedChatHistory: ChatMessage[] = [];
            historyResponse.forEach((response) => {
                let ds: { name: string, data: string, url: string | undefined }[] = [];
                response?.toolsCalled.forEach((tool: ToolCalled) => {
                    for (let key in tool.toolArguments) {
                        ds.push({ name: tool.toolName, data: tool.toolArguments[key], url: tool.url });
                    }
                });
                updatedChatHistory.push({
                    role: response.role,
                    message: response.message,
                    // Use animated text for assistant messages, regular for user messages
                    elements: response.role === "assistant"
                        ? [getAnimatedText(response.message, false)] // Don't animate historical messages
                        : [getAnimatedText(response.message, false)],
                    id: response.id,
                    canBeRated: response.canBeRated,
                    sessionId: response.sessionId,
                    queryId: response.queryId,
                    dataSources: Object.keys(ds).length > 0 ? ds : undefined
                });
            });

            if (updatedChatHistory.length > 0) {
                const pendingQueryId = getPendingQueryId(historyResponse);
                if (!pendingQueryId) {
                    setChatHistory(updatedChatHistory);
                    setIsLoadingNewHistory(false);
                    return;
                }

                const queryId = pendingQueryId;
                const placeholderId = `progress-${queryId}`;
                const controller = new AbortController();
                requestControllerRef.current?.abort();
                requestControllerRef.current = controller;
                activeRequestIdRef.current = queryId;
                setIsLoading(true);
                setChatHistory([...updatedChatHistory, {
                    role: "assistant",
                    message: "showSpinner",
                    elements: [getAnimatedText("Thinking...", true, undefined, scrollToBottomThrottled)],
                    id: placeholderId,
                    canBeRated: false,
                    sessionId: -1
                }]);
                setIsLoadingNewHistory(false);

                try {
                    const response = await getChatProgress(userId, props.sessionId ?? 0, queryId, (progress) => {
                        if (activeRequestIdRef.current !== queryId) return;
                        setChatHistory((prevHistory) => prevHistory.map((chat) =>
                            chat.id === placeholderId ? { ...chat, progress } : chat
                        ));
                    }, controller.signal);
                    if (activeRequestIdRef.current !== queryId) return;

                    const dataSources: { name: string, data: string, url: string | undefined }[] = [];
                    response.toolsCalled.forEach((tool: ToolCalled) => {
                        for (let key in tool.toolArguments) {
                            dataSources.push({ name: tool.toolName, data: tool.toolArguments[key], url: tool.url });
                        }
                    });
                    setChatHistory((prevHistory) => prevHistory.map((chat) =>
                        chat.id === placeholderId ? {
                            id: response.id,
                            role: "assistant",
                            message: response.message,
                            dataSources: dataSources.length > 0 ? dataSources : undefined,
                            elements: [getAnimatedText(response.message, true, undefined, scrollToBottomThrottled)],
                            canBeRated: response.canBeRated,
                            sessionId: response.sessionId,
                        } : chat
                    ));
                } catch (error) {
                    if (activeRequestIdRef.current !== queryId || (error as { name?: string })?.name === "AbortError") return;
                    const errorMessage = error instanceof Error ? error.message : "I'm sorry, I'm currently unavailable. Please try again later.";
                    setChatHistory((prevHistory) => prevHistory.map((chat) =>
                        chat.id === placeholderId ? {
                            ...chat,
                            id: `error-${queryId}`,
                            message: errorMessage,
                            elements: [getAnimatedText(errorMessage, false)],
                            progress: undefined,
                        } : chat
                    ));
                } finally {
                    if (activeRequestIdRef.current === queryId) {
                        activeRequestIdRef.current = null;
                        requestControllerRef.current = null;
                        setIsLoading(false);
                    }
                }
            } else {
                const response = await getAssistantGreetings();
                setChatHistory([{
                    role: "assistant",
                    message: response.message,
                    elements: [getAnimatedText(response.message, true, undefined, scrollToBottomThrottled)],
                    id: response.id,
                    canBeRated: response.canBeRated,
                    sessionId: response.sessionId
                }]);
                setIsLoadingNewHistory(false);
            }
        })();
    }, [props.sessionId, scrollToBottomThrottled]);

    useEffect(() => {
        setIsLoading(false);
        return () => {
            activeRequestIdRef.current = null;
            requestControllerRef.current?.abort();
            requestControllerRef.current = null;
        };
    }, [props.sessionId]);

    const setCanBeRated = (id: string, canBeRated: boolean) => {
        setChatHistory((prevHistory) =>
            prevHistory.map((chat) =>
                chat.id === id ? { ...chat, canBeRated } : chat
            )
        );
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);
    };

    const handleSubmit = async () => {
        const messageToSend = message;
        if (!messageToSend.trim() || isLoading || props.sessionId === undefined) return;

        requestControllerRef.current?.abort();
        const controller = new AbortController();
        const requestId = uuidv4();
        const placeholderId = `progress-${requestId}`;
        requestControllerRef.current = controller;
        activeRequestIdRef.current = requestId;

        setIsLoading(true);

        // Create a complete new history with the completed previous messages
        // This interrupts any ongoing animations by replacing elements with non-animated versions
        const completedHistory = chatHistory.map(chat => ({
            ...chat,
            elements: chat.role === "assistant"
                ? [getAnimatedText(chat.message, false)] // Force completion of animation
                : chat.elements
        }));

        var newHistory = [...completedHistory, {
            role: "user",
            message: messageToSend,
            elements: [getAnimatedText(messageToSend, false)],
            id: uuidv4(),
            canBeRated: false,
            sessionId: -1
        }];
        setChatHistory(newHistory);
        setMessage('');

        // Add thinking indicator
        setChatHistory([...newHistory, {
            role: "assistant",
            message: "showSpinner",
            elements: [getAnimatedText("Thinking...", true, undefined, scrollToBottomThrottled)],
            id: placeholderId,
            canBeRated: false,
            sessionId: -1
        }]);

        try {
            const response = await postChatMessage(
                userId,
                props.sessionId,
                messageToSend,
                (progress) => {
                    if (activeRequestIdRef.current !== requestId) return;
                    setChatHistory((prevHistory) => prevHistory.map((chat) =>
                        chat.id === placeholderId ? { ...chat, progress } : chat
                    ));
                },
                controller.signal
            );
            if (activeRequestIdRef.current !== requestId) return;

            const dataSources: { name: string, data: string, url: string | undefined }[] = [];
            response.toolsCalled.forEach((tool: ToolCalled) => {
                for (let key in tool.toolArguments) {
                    dataSources.push({ name: tool.toolName, data: tool.toolArguments[key], url: tool.url });
                }
            });

            const responseMessage: ChatMessage = {
                id: response.id,
                role: "assistant",
                message: response.message,
                dataSources: dataSources.length > 0 ? dataSources : undefined,
                elements: [getAnimatedText(response.message, true, undefined, scrollToBottomThrottled)],
                canBeRated: response.canBeRated,
                sessionId: response.sessionId,
            };
            setChatHistory((prevHistory) => prevHistory.map((chat) =>
                chat.id === placeholderId ? responseMessage : chat
            ));
        } catch (error) {
            if (activeRequestIdRef.current !== requestId || (error as { name?: string })?.name === "AbortError") return;

            const errorMessage = error instanceof Error
                ? error.message
                : "I'm sorry, I'm currently unavailable. Please try again later.";
            setChatHistory((prevHistory) => prevHistory.map((chat) =>
                chat.id === placeholderId
                    ? {
                        ...chat,
                        id: `error-${requestId}`,
                        message: errorMessage,
                        elements: [getAnimatedText(errorMessage, false)],
                        progress: undefined,
                    }
                    : chat
            ));
        } finally {
            if (activeRequestIdRef.current === requestId) {
                activeRequestIdRef.current = null;
                requestControllerRef.current = null;
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="w-11/12 md:w-4/6 h-screen place-self-start pb-10 flex flex-col">
            {isLoadingNewHistory === false ?
                <div className="overflow-y-auto flex-grow flex flex-col-reverse gap-5 md:gap-0" ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
                    {
                        chatHistory.slice().reverse().map((chat, index) => (
                            <div className={chat.role === "assistant" ?
                                "place-self-center md:place-self-start w-11/12 md:w-6/12 md:pr-8 " :
                                "place-self-center md:place-self-end w-11/12 md:w-6/12 md:pl-8"} key={index}>
                                <ChatBubble
                                    id={chat.id}
                                    message={chat.message}
                                    role={chat.role}
                                    dataSources={chat.dataSources}
                                    animate={chat.role === "assistant" && index === 0}
                                    elements={chat.elements}
                                    canBeRated={chat.canBeRated}
                                    sessionId={chat.sessionId}
                                    setCanBeRated={setCanBeRated}
                                    progress={chat.progress}
                                />
                            </div>
                        ))
                    }
                </div> : <Spinner className="absolute place-self-center h-full" size="extra-large" />}
            <div className="mt-12 w-11/12 md:w-6/12 place-self-center">
                <span className="flex text-text-light dark:text-text-dark">
                    <input
                        type="text"
                        placeholder="Ask me anything"
                        value={message}
                        onChange={handleInputChange}
                        onKeyDown={(e) => e.key === 'Enter' && message.trim() !== '' && !isLoading && handleSubmit()}
                        className="fixed text-primary-dark dark:text-text-dark text-sm focus:border-blue-500 block
                            md:left-1/4 md:right-1/4 left-11 right-11 bottom-5 p-2.5 shadow-lg"
                    />
                    <div className="fixed md:right-1/4 right-12 bottom-7 px-3">
                        <div className="flex justify-end">
                            {isLoading ?
                                <p>...</p> :
                                <button onClick={handleSubmit} disabled={message.trim() === ''}
                                    className="hover:bg-primary-dark/10 dark:hover:bg-primary-light/10 hover:scale-110 transition-transform duration-200">
                                    <Send20Regular />
                                </button>
                            }
                        </div>
                    </div>
                </span>
            </div >
        </div >
    );
}